import type { ResponseOf } from '@shared/contracts';
import type { z } from 'zod';
import type { schemas } from '@shared/index';
import { ALWAYS_ON } from './labels';

type Preferences = ResponseOf<'notifications.preferences.get'>['data'];
type Category = z.infer<typeof schemas.comms.notification>['category'];

export type RealtimeNotificationPayload = {
  id: string;
  eventKey: string;
  category: Category;
  title: string;
  body: string;
  redirectUrl: string | null;
};

const ICON = '/icons/icon-192.png';

export async function requestBrowserNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function showBrowserNotification(options: {
  title: string;
  body: string;
  tag?: string;
  url?: string | null;
}): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      tag: options.tag,
      icon: ICON,
      badge: ICON,
    });

    if (options.url) {
      notification.onclick = () => {
        window.focus();
        window.location.assign(options.url!);
        notification.close();
      };
    }
  } catch {
    // Safari and locked-down profiles can throw even after `granted`.
  }
}

export function shouldShowBrowserPopup(
  payload: RealtimeNotificationPayload,
  preferences: Preferences | undefined,
): boolean {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false;
  }
  if (!preferences?.channels.webPush) return false;

  const categoryOn =
    ALWAYS_ON.includes(payload.category) || preferences.categories[payload.category] !== false;
  if (!categoryOn) return false;

  if (ALWAYS_ON.includes(payload.category)) return true;
  return !inQuietHours(preferences);
}

function inQuietHours(preferences: Preferences, now: Date = new Date()): boolean {
  if (!preferences.quietHours.enabled) return false;

  const minutes = now.getHours() * 60 + now.getMinutes();
  const from = toMinutes(preferences.quietHours.from);
  const to = toMinutes(preferences.quietHours.to);
  if (from === null || to === null) return false;

  return from <= to ? minutes >= from && minutes < to : minutes >= from || minutes < to;
}

function toMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

export function welcomeAfterLogin(user: ResponseOf<'auth.login'>['data']): void {
  const first =
    user.employee?.fullName?.split(/\s+/)[0] ?? user.email.split('@')[0] ?? 'there';
  showBrowserNotification({
    title: 'Signed in to BM-Work',
    body: `Welcome back, ${first}. You will get browser alerts for new notifications here.`,
    tag: 'bemedic.auth.login',
  });
}
